import {
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { ModalConfirm } from "@src/components/ui-kit/Modals/ModalConfirm";
import { useFiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { observer } from "mobx-react-lite";

export const FilterTemplateEditModal = observer(
  function FilterTemplateEditModal() {
    const { filters } = useFiltersPanel();
    const pendingDelete = filters.deleteTemplateModal.additionalData;

    return (
      <>
        <Modal
          isOpen={filters.editTemplatesModal.isOpen}
          onClose={() => {
            if (filters.isSavingTemplate) return;
            filters.editTemplatesModal.onClose();
          }}
          scrollBehavior="inside"
          size="3xl"
          trapFocus={false}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <Trans>Edit filter templates</Trans>
            </ModalHeader>
            <ModalCloseButton isDisabled={filters.isSavingTemplate} />
            <ModalBody pb="6">
              {filters.templates.length === 0 ? (
                <Text color="grey.500" textAlign="center">
                  <Trans>No saved filter templates.</Trans>
                </Text>
              ) : (
                <VStack align="stretch" spacing="3">
                  {filters.templates.map((template) => {
                    const draftName =
                      filters.templateNameDrafts[template.id] ?? template.name;
                    const isUnchanged = draftName.trim() === template.name;

                    return (
                      <HStack key={template.id} spacing="2">
                        <Input
                          isDisabled={filters.isSavingTemplate}
                          onChange={(event) => {
                            filters.setTemplateNameDraft(
                              template.id,
                              event.target.value,
                            );
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter") return;
                            filters.saveTemplateName(template.id);
                          }}
                          value={draftName}
                        />
                        <IconButton
                          aria-label={t`Save filter template`}
                          colorScheme="green"
                          icon={<Icon name="check" />}
                          isDisabled={isUnchanged || filters.isSavingTemplate}
                          onClick={() => {
                            filters.saveTemplateName(template.id);
                          }}
                          size="sm"
                          variant="ghost"
                        />
                        <IconButton
                          aria-label={t`Delete filter template`}
                          colorScheme="red"
                          icon={<Icon name="trash-03" />}
                          isDisabled={
                            filters.isDeletingTemplate ||
                            filters.isSavingTemplate
                          }
                          onClick={() => {
                            filters.deleteTemplateModal.onOpen({
                              id: template.id,
                              name: draftName,
                            });
                          }}
                          size="sm"
                          variant="ghost"
                        />
                      </HStack>
                    );
                  })}
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        <ModalConfirm
          destructive
          heading={t`Delete filter template`}
          isLoading={filters.isDeletingTemplate}
          isOpen={filters.deleteTemplateModal.isOpen}
          message={
            pendingDelete
              ? t`Are you sure you want to delete "${pendingDelete.name}"?`
              : undefined
          }
          onClose={filters.deleteTemplateModal.onClose}
          onConfirm={() => {
            if (!pendingDelete) return;
            return filters.deleteTemplate(pendingDelete.id);
          }}
        />
      </>
    );
  },
);
